import { useEffect, useState } from "react";
import { Users, Stethoscope, Hospital, Calendar, IndianRupee, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import AdminLayout from "../components/AdminLayout";
import { api } from "../lib/api";

function Tile({ label, value, icon: Icon, accent }) {
  return (
    <div data-testid={`admin-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`} className="bg-white border border-border rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <span className={"h-10 w-10 rounded-xl flex items-center justify-center " + (accent || "bg-accent text-accent-foreground")}>
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="text-2xl font-semibold tracking-tight">{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <AdminLayout title="Dashboard" subtitle="Platform KPIs and revenue overview">
      <div data-testid="admin-dashboard" className="space-y-6">
        {loading || !data ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <div key={i} className="h-24 bg-muted rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Tile label="Patients" value={data.total_patients} icon={Users} />
              <Tile label="Doctors" value={`${data.active_doctors}/${data.total_doctors}`} icon={Stethoscope} />
              <Tile label="Clinics" value={data.total_clinics} icon={Hospital} />
              <Tile label="Appointments" value={data.appointments_total} icon={Calendar} />
              <Tile label="Today" value={data.appointments_today} icon={Calendar} accent="bg-amber-50 text-amber-700" />
              <Tile label="Completed" value={data.completed_consultations} icon={TrendingUp} accent="bg-emerald-50 text-emerald-700" />
              <Tile label="Revenue total" value={`₹${data.revenue_total.toLocaleString("en-IN")}`} icon={IndianRupee} accent="bg-emerald-50 text-emerald-700" />
              <Tile label="Revenue month" value={`₹${data.revenue_this_month.toLocaleString("en-IN")}`} icon={IndianRupee} accent="bg-emerald-50 text-emerald-700" />
            </div>

            <section data-testid="admin-revenue-chart" className="bg-white border border-border rounded-2xl p-5 shadow-sm">
              <h2 className="font-semibold tracking-tight mb-3">Revenue (last 30 days)</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.revenue_by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="revenue" stroke="hsl(179 84% 35%)" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section data-testid="admin-top-doctors" className="bg-white border border-border rounded-2xl p-5 shadow-sm">
              <h2 className="font-semibold tracking-tight mb-3">Top 5 doctors</h2>
              {data.top_doctors.length === 0 ? (
                <div className="text-sm text-muted-foreground">No completed appointments yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr><th className="text-left py-2">Doctor</th><th className="text-right py-2">Appointments</th><th className="text-right py-2">Revenue</th></tr>
                  </thead>
                  <tbody>
                    {data.top_doctors.map((d) => (
                      <tr key={d.doctor_id} className="border-t border-border">
                        <td className="py-2">{d.name}</td>
                        <td className="py-2 text-right tabular-nums">{d.appointments}</td>
                        <td className="py-2 text-right tabular-nums">₹{d.revenue.toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section data-testid="admin-specializations" className="bg-white border border-border rounded-2xl p-5 shadow-sm">
              <h2 className="font-semibold tracking-tight mb-3">Specialization breakdown</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {data.specialization_breakdown.map((s) => (
                  <div key={s.category} className="bg-secondary/60 rounded-xl p-3 text-sm">
                    <div className="font-medium">{s.category}</div>
                    <div className="text-muted-foreground">{s.count} doctor(s)</div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
